import AssemblyInstructions from "@NA/Icons/PDPDocuments/AssemblyInstructions";
import DataSheetIcon from "@NA/Icons/PDPDocuments/DataSheetIcon";
import EducationalBrochure from "@NA/Icons/PDPDocuments/EducationalBrochure";
import EnergyGuide from "@NA/Icons/PDPDocuments/EnergyGuide";
import ManufacturerBrochure from "@NA/Icons/PDPDocuments/ManufacturerBrochure";
import UserManualIcon from "@NA/Icons/PDPDocuments/UserManualIcon";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { css } from "styled-components";

const enum documents {
    productManual = "Product Manual",
    userManual = "User Manual",
    assemblyInstructions = "Assembly Instructions",
    manufacturerBrochure = "Manufacturer Brochure",
    energyGuide = "Energy Guide",
    educationalBrochure = "Educational Brochure",
}

// Refered based on classic and Prod
const documentMasterOrder: string[] = ["Product Manual", "User Manual", "Assembly Instructions", "Manufacturer Brochure", "Energy Guide", "Educational Brochure"];

type OwnProps = WidgetProps & HasProduct;

export interface ProductDetailsDocumentsStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    documentNameResource?: TypographyPresentationProps
}

export const documentsStyles: ProductDetailsDocumentsStyles = {
    wrapper: {
        css: css`
            padding-top: 10px ;
            padding-bottom: 35px;
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [null, null, css` padding-bottom: 10px; `,],"max",); 
            }}
        `,
    },
    documentNameResource: {
        css: css`
          padding: 6px 18px 0 0; 
        `
    },
    container: {
        gap: 10,
        css: css`
            > ${GridOffset} {
                margin: 0;
                max-width: 100%;
            }
        `,
    },
    documentGridItem: {
        width: [6, 6, 6, 2, 2],
        css: css`
            border-radius: 4px;
            background-color: ${ADIColors.white};
            border: 1px solid ${ADIColors.gray2};
            padding: 0;
            margin: 0;
            margin-bottom: 0;
            margin-right: 20px !important;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                        flex-basis: calc(50% - 10px) !important;
                        max-width: calc(50% - 10px) !important;
                        margin-bottom: 20px !important;
                        &:nth-child(even) {
                            margin-right: 0px !important;
                        }
                    `,
                        css`
                        flex-basis: calc(50% - 10px) !important;
                        max-width: calc(50% - 10px) !important;
                        margin-bottom: 20px !important;
                        &:nth-child(even) {
                            margin-right: 0px !important;
                        }
                    `,
                        css`
                        flex-basis: calc(50% - 10px) !important;
                        max-width: calc(50% - 10px) !important;
                        margin-bottom: 20px !important;
                        &:nth-child(even) {
                            margin-right: 0px !important;
                        }
                    `,
                        css`
                        flex-basis: calc(16.666667% - (80px/4)) !important;
                        max-width: calc(16.666667% - (80px/4)) !important;
                        &:nth-child(6) {
                            margin-right: 0px !important;
                        }
                    `,
                        css`
                        flex-basis: calc(16.666667% - (80px/4)) !important;
                        max-width: calc(16.666667% - (80px/4)) !important;
                        &:nth-child(6) {
                            margin-right: 0px !important;
                        }
                    `,
                    ]
                )}
            
        `,
    },
    documentIcon: {
        color: "text.link",
        css: css`
            margin-bottom: 16px;
            color: ${ADIColors.blue5} !important;
        `,
    },
    documentLink: {
        css: css`
            border: 0;
            background-color: #fff;
            bottom: 0;
            width: 100%;
            padding: 0;
            padding-bottom: 20px;
            padding-top: 16px;
            border-radius: 4px;
            display: block;
            > span {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
            }
            &:hover {
                text-decoration: underline;
                color: ${ADIColors.blue5} !important;
                > span {
                    color: ${ADIColors.blue5} !important;
                }
            }
        `,
    },
    documentNameText: {
        weight: 800,
        size: 14,
        css: css`
            text-align: center;
            &:hover {
                text-decoration: underline;
                color: ${ADIColors.blue5} !important;;
            }
        `,
    },
};

const styles = documentsStyles;

const GetDocumentIcon = (icon: string) => {
    switch (icon) {
        case documents.manufacturerBrochure:
            return ManufacturerBrochure;
        case documents.productManual:
            return DataSheetIcon;
        case documents.userManual:
            return UserManualIcon;
        case documents.assemblyInstructions:
            return AssemblyInstructions;
        case documents.educationalBrochure:
            return EducationalBrochure;
        case documents.energyGuide:
            return EnergyGuide;
        default:
            break;
    }

    return "";
};

const GetIconWidth = (width: string) => {
    switch (width) {
        case documents.manufacturerBrochure:
            return 45;
        case documents.productManual:
            return 37;
        case documents.userManual:
            return 33;
        case documents.energyGuide:
            return 47;
        case documents.assemblyInstructions:
            return 47;
        case documents.educationalBrochure:
            return 41;
        default:
            break;
    }

    return 60;
};

const GetDocumentName = (name: string) => {
    switch (name) {
        case documents.productManual:
            return "Product Data Sheet";
        case documents.assemblyInstructions:
            return "Installation Instructions";
        case documents.educationalBrochure:
            return "Additional Resources";
        case documents.energyGuide:
            return "Certification / Declaration";
        default:
            break;
    }

    return name;
};

const ProductDetailsDocuments: React.FC<OwnProps> = ({ product: { documents } }) => {
    const modifyDocOrder = (docs:OwnProps["product"]["documents"]) => docs?.sort((a,b) => documentMasterOrder.indexOf(a["name"]) > documentMasterOrder.indexOf(b["name"]) ? 1 : -1);

    return (
        <StyledWrapper {...styles.wrapper}>
            {documents && documents.length ? (
                <Typography variant="h3" as="h3" data-test-selector="DocumentsTitle" {...styles.documentNameResource}>
                    {translate("Product Documents and Resources")}
                </Typography>
            ) : null}
            <GridContainer {...styles.container}>
                {documents && modifyDocOrder([...documents])?.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo width={GetIconWidth(document.name)} height={48} src={GetDocumentIcon(document.name)} {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>
                                {GetDocumentName(document.name)}
                            </Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDocuments),
    definition: {
        displayName: "Documents",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
